import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

import { MinMaxArea, PremisesCookieModel, PremisesType } from 'components/pages/PremisesStartPage/types';
import { AREA_FILTER_MAX, AREA_FILTER_MIN } from 'features/premises/constants';
import { Tag } from 'features/search/types';
import { CookieStore } from 'features/cookies/cookie-store';
import { SortBy } from 'features/search/filters/enums';

const premisesCookie: PremisesCookieModel | null = CookieStore.getPremisesFilters();

export interface PremisesState {
    tags: Tag[];
    types: PremisesType[];
    minMaxArea: MinMaxArea;
    sortBy: SortBy;
}

interface PremisesActions {
    setPremisesMinMax: (minMax: MinMaxArea) => void;
    setPremisesTypes: (types: PremisesType[]) => void;
    setPremisesSortBy: (sortBy: SortBy) => void;
    setPremisesTags: (tags: Tag[]) => void;
    resetPremisesFilters: () => void;
}

const initialState: PremisesState = {
    tags: premisesCookie?.tags ?? [],
    types: premisesCookie?.types ?? [],
    minMaxArea: premisesCookie?.minMaxArea ?? { min: AREA_FILTER_MIN, max: AREA_FILTER_MAX },
    sortBy: premisesCookie?.sort ?? SortBy.PREMISES_ACCESS_ASC,
};

export const usePremisesStore = create<PremisesState & PremisesActions>()(
    subscribeWithSelector((set) => ({
        ...initialState,

        setPremisesMinMax: (minMax) => set({ minMaxArea: minMax }),

        setPremisesTypes: (types) => set({ types }),

        setPremisesSortBy: (sortBy) => set({ sortBy }),

        setPremisesTags: (tags) => set({ tags }),

        resetPremisesFilters: () => set({
            minMaxArea: { min: AREA_FILTER_MIN, max: AREA_FILTER_MAX },
            tags: [],
            sortBy: SortBy.PREMISES_ACCESS_ASC,
            types: [],
        }),
    }))
);

// Cookie persistence subscription
usePremisesStore.subscribe(
    (state) => ({
        minMaxArea: state.minMaxArea,
        tags: state.tags,
        types: state.types,
        sortBy: state.sortBy,
    }),
    (filterState) => {
        const model: PremisesCookieModel = {
            minMaxArea: filterState.minMaxArea,
            tags: filterState.tags,
            types: filterState.types,
            sort: filterState.sortBy,
        };
        CookieStore.setPremisesFilters(model);
    },
    { equalityFn: (a, b) => JSON.stringify(a) === JSON.stringify(b) }
);

// Selectors
export const selectPremisesTags = (state: PremisesState & PremisesActions) => state.tags;
export const selectPremisesTypes = (state: PremisesState & PremisesActions) => state.types;
export const selectPremisesMinMaxArea = (state: PremisesState & PremisesActions) => state.minMaxArea;
export const selectPremisesSortBy = (state: PremisesState & PremisesActions) => state.sortBy;
